/**
 * Alabama State-Specific Diagnostic Questions
 * 
 * IMPORTANT REGULATORY CONTEXT:
 * Alabama does NOT have state licensure requirements for home health agencies.
 * Per Ala. Admin. Code r. 410-2-4-.07: "There is no licensure requirement for home health agencies in Alabama."
 * 
 * Alabama home health agencies operate under:
 * 1. Medicare/Medicaid Certification (42 CFR 484) - for agencies billing Medicare/Medicaid
 * 2. Certificate of Need (CON) - required from SHPDA for Medicare-certified agencies
 * 3. Federal requirements (OSHA, HIPAA, etc.)
 * 4. Alabama Medicaid Agency requirements for Medicaid providers
 * 
 * This diagnostic focuses on federal Conditions of Participation and Alabama-specific
 * requirements where they exist (Medicaid, employment law, abuse reporting).
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const ALABAMA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: CERTIFICATION & OPERATIONS
  // ============================================
  {
    id: 'AL-A1',
    itemId: 'A1',
    stateCode: 'AL',
    title: 'Medicare Certification Status',
    description: 'Alabama does not require state licensure for home health agencies. Medicare-certified agencies must comply with federal Conditions of Participation (42 CFR 484).',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoP',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - Medicare certified' },
          { value: 'no', label: 'No - Not Medicare certified' },
        ],
        required: true,
        helpText: 'Medicare certification requires meeting federal Conditions of Participation and passing CMS survey.',
      },
      {
        id: 'q2',
        question: 'When was your last Medicare recertification survey?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: '1_3_years', label: '1-3 years ago' },
          { value: 'over_3', label: 'Over 3 years ago' },
          { value: 'initial', label: 'Awaiting initial survey' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'What is your CMS Certification Number (CCN)?',
        inputType: 'text',
        helpText: 'Your 6-digit CCN identifies your agency with Medicare. Format: 01-XXXX',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency maintains Medicare certification and is subject to federal Conditions of Participation (42 CFR 484).',
      atRisk: 'Your agency is not Medicare certified. In Alabama, there is no state licensure requirement, but Medicare certification is required to bill Medicare/Medicaid and demonstrates compliance with federal quality standards.',
      nonCompliant: 'Consider obtaining Medicare certification to expand service eligibility and demonstrate quality standards.',
      unknown: 'Verify your Medicare certification status with CMS.',
    },
    stateNotes: [
      'Alabama does NOT require state licensure for home health agencies',
      'Medicare-certified agencies must comply with 42 CFR 484',
      'Certificate of Need (CON) required from SHPDA for Medicare-certified agencies',
      'Non-certified agencies have minimal state oversight',
    ],
  },

  {
    id: 'AL-A2',
    itemId: 'A2',
    stateCode: 'AL',
    title: 'Certificate of Need (CON) Status',
    description: 'Alabama requires a Certificate of Need from SHPDA for Medicare-certified home health agencies',
    severity: 'high',
    regulationCode: '22-21-260',
    regulationName: 'Certificate of Need',
    agencyName: 'State Health Planning and Development Agency',
    agencyAbbr: 'SHPDA',
    agencyPhone: '(334) 242-4103',
    agencyUrl: 'https://shpda.alabama.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a Certificate of Need (CON) from SHPDA?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - Current CON on file' },
          { value: 'pending', label: 'CON application pending' },
          { value: 'no', label: 'No CON' },
          { value: 'exempt', label: 'Exempt (non-Medicare agency)' },
        ],
        required: true,
        helpText: 'CON is required for Medicare-certified home health agencies in Alabama.',
      },
      {
        id: 'q2',
        question: 'What counties are included in your CON service area?',
        inputType: 'text',
        helpText: 'List the counties authorized in your Certificate of Need',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'exempt', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Certificate of Need status is current with SHPDA.',
      atRisk: 'CON application is pending. Do not provide Medicare services until CON is approved.',
      nonCompliant: 'Medicare-certified home health agencies in Alabama require a Certificate of Need. Contact SHPDA at (334) 242-4103.',
      unknown: 'Verify CON requirements with SHPDA.',
    },
    stateNotes: [
      'CON required per Code of Alabama Section 22-21-260 for Medicare-certified agencies',
      'SHPDA administers CON program: shpda.alabama.gov',
      'CON specifies authorized service area by county',
      'Operating without approved CON may result in sanctions',
    ],
  },

  {
    id: 'AL-A3',
    itemId: 'A3',
    stateCode: 'AL',
    title: 'Alabama Medicaid Enrollment',
    description: 'Agencies billing Alabama Medicaid must be enrolled as Medicaid providers',
    severity: 'high',
    regulationCode: 'AMA Admin Code Ch. 12',
    regulationName: 'Medicaid Enrollment',
    agencyName: 'Alabama Medicaid Agency',
    agencyAbbr: 'AMA',
    agencyPhone: '(334) 242-5000',
    agencyUrl: 'https://medicaid.alabama.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency enrolled as an Alabama Medicaid provider?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have Electronic Visit Verification (EVV) implemented?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - EVV operational' },
          { value: 'no', label: 'No - Not implemented' },
        ],
        required: true,
        helpText: 'Alabama Medicaid requires EVV for home health services per federal 21st Century Cures Act.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Alabama Medicaid enrollment is current with EVV implemented.',
      atRisk: 'Consider enrolling as Alabama Medicaid provider to expand patient access.',
      nonCompliant: 'EVV is required for Alabama Medicaid home health services. Implement EVV immediately to avoid claim denials.',
      unknown: 'Verify Medicaid enrollment status with Alabama Medicaid Agency.',
    },
    stateNotes: [
      'Alabama Medicaid Agency: medicaid.alabama.gov - (334) 242-5000',
      'EVV required per 21st Century Cures Act for personal care and home health services',
      'Alabama uses Sandata as state-selected EVV vendor',
      'Medicaid providers must re-enroll every 5 years',
    ],
  },

  {
    id: 'AL-A4',
    itemId: 'A4',
    stateCode: 'AL',
    title: 'Administrator Qualifications (42 CFR 484.105)',
    description: 'Medicare Conditions of Participation require qualified administrator oversight',
    severity: 'high',
    regulationCode: '42 CFR 484.105',
    regulationName: 'Administrator',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet federal qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'licensed', label: 'Licensed physician, or' },
          { value: 'rn', label: 'Registered Nurse, or' },
          { value: 'degree_exp', label: 'Has degree in health-related field with supervisory experience' },
        ],
        required: true,
        helpText: 'Per 42 CFR 484.105, administrator must be a licensed physician, RN, or have appropriate healthcare management credentials.',
      },
      {
        id: 'q2',
        question: 'Does the administrator have documented supervisory/administrative experience in home health or healthcare?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - documented experience' },
          { value: 'no', label: 'No / not documented' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 1 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet federal requirements per 42 CFR 484.105.',
      atRisk: 'Document administrator experience and qualifications in personnel file.',
      nonCompliant: 'Administrator does not meet 42 CFR 484.105 qualifications. This is a Condition-level deficiency for Medicare certification.',
      unknown: 'Verify administrator qualifications against 42 CFR 484.105.',
    },
    stateNotes: [
      'Alabama defers to federal 42 CFR 484.105 for administrator qualifications',
      'Must be physician, RN, or hold degree with supervisory experience',
      'Responsible for day-to-day agency operations',
      'Qualifications and experience must be documented in personnel file',
    ],
  },

  {
    id: 'AL-A5',
    itemId: 'A5',
    stateCode: 'AL',
    title: 'Clinical Manager/Supervising Nurse (42 CFR 484.115)',
    description: 'Medicare requires a qualified supervising physician or RN for clinical oversight',
    severity: 'high',
    regulationCode: '42 CFR 484.115',
    regulationName: 'Clinical Manager',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a designated clinical manager who is a licensed physician or RN?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the clinical manager have current Alabama licensure?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - verified current' },
          { value: 'no', label: 'No / not verified' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical manager meets 42 CFR 484.115 requirements with current Alabama licensure.',
      atRisk: 'Verify clinical manager Alabama licensure at abn.alabama.gov.',
      nonCompliant: 'No qualified clinical manager designated. This is a Condition-level deficiency.',
      unknown: 'Verify clinical manager qualifications.',
    },
    stateNotes: [
      'Alabama Board of Nursing license verification: abn.alabama.gov',
      'Must be licensed physician or RN per 42 CFR 484.115',
      'Responsible for clinical oversight of patient care',
      'Must maintain current Alabama license in good standing',
    ],
  },

  {
    id: 'AL-A6',
    itemId: 'A6',
    stateCode: 'AL',
    title: 'Liability Insurance Coverage',
    description: 'Professional liability insurance is essential for risk management',
    severity: 'high',
    regulationCode: 'Best Practice',
    regulationName: 'Liability Insurance',
    agencyName: 'N/A',
    agencyAbbr: 'N/A',
    agencyPhone: 'N/A',
    agencyUrl: 'N/A',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain professional liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your liability coverage amount per occurrence?',
        inputType: 'select',
        options: [
          { value: '1m_plus', label: '$1,000,000 or more' },
          { value: '500k_1m', label: '$500,000 - $999,999' },
          { value: 'under_500k', label: 'Under $500,000' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'When does your current liability policy expire?',
        inputType: 'date',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: '1m_plus' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'under_500k', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Professional liability insurance coverage is adequate.',
      atRisk: 'Consider increasing liability coverage to at least $1,000,000 per occurrence.',
      nonCompliant: 'Operating without liability insurance creates significant legal and financial risk. Obtain coverage immediately.',
      unknown: 'Review your insurance policy and verify adequate coverage.',
    },
    stateNotes: [
      'While Alabama does not mandate specific insurance amounts for home health, adequate coverage is essential',
      'Medicare certification and most contracts require proof of liability insurance',
    ],
  },

  {
    id: 'AL-A7',
    itemId: 'A7',
    stateCode: 'AL',
    title: 'Workers Compensation Insurance',
    description: 'Alabama requires workers compensation for employers with 5 or more employees',
    severity: 'high',
    regulationCode: '25-5-1',
    regulationName: 'Workers Compensation',
    agencyName: 'Alabama Department of Labor',
    agencyAbbr: 'ADOL',
    agencyPhone: '(334) 242-8990',
    agencyUrl: 'https://labor.alabama.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How many employees does your agency have?',
        inputType: 'select',
        options: [
          { value: '5_plus', label: '5 or more employees' },
          { value: 'under_5', label: 'Fewer than 5 employees' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have current workers compensation insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '5_plus' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'under_5' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation insurance coverage meets Alabama requirements.',
      atRisk: 'Even with fewer than 5 employees, workers compensation insurance is recommended for home health agencies.',
      nonCompliant: 'Alabama law requires workers compensation for employers with 5 or more employees. Obtain coverage immediately to avoid penalties.',
      unknown: 'Verify workers compensation requirements with Alabama Department of Labor.',
    },
    stateNotes: [
      'Code of Alabama Section 25-5-1 requires workers comp for 5+ employees',
      'Alabama Department of Labor: labor.alabama.gov - (334) 242-8990',
      'Home health staff face injury risks; coverage strongly recommended',
      'Penalties for non-compliance include fines and business closure',
    ],
  },

  {
    id: 'AL-A8',
    itemId: 'A8',
    stateCode: 'AL',
    title: 'Business Entity Registration',
    description: 'Proper business registration with Alabama Secretary of State',
    severity: 'medium',
    regulationCode: 'Title 10A',
    regulationName: 'Business Registration',
    agencyName: 'Alabama Secretary of State',
    agencyAbbr: 'SOS',
    agencyPhone: '(334) 242-7200',
    agencyUrl: 'https://www.sos.alabama.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is your business entity properly registered with the Alabama Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your business in good standing with the state?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Business entity is properly registered and in good standing with Alabama.',
      atRisk: 'Verify business registration status with the Secretary of State.',
      nonCompliant: 'Business registration issues can affect contracts and Medicare enrollment. Contact Secretary of State at (334) 242-7200.',
      unknown: 'Check your business status at sos.alabama.gov.',
    },
    stateNotes: [
      'Alabama Secretary of State: sos.alabama.gov - (334) 242-7200',
      'Business entities must file annual reports to maintain good standing',
      'LLCs, corporations, and partnerships require state registration',
      'Good standing required for Medicare enrollment and contracts',
    ],
  },

  {
    id: 'AL-A9',
    itemId: 'A9',
    stateCode: 'AL',
    title: 'Emergency Preparedness (42 CFR 484.102)',
    description: 'Medicare requires comprehensive emergency preparedness planning',
    severity: 'high',
    regulationCode: '42 CFR 484.102',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written emergency preparedness plan?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required elements does your emergency plan address? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'risk_assessment', label: 'Risk assessment (all-hazards approach)' },
          { value: 'communication', label: 'Communication plan with patients and staff' },
          { value: 'policies', label: 'Emergency policies and procedures' },
          { value: 'training', label: 'Training and testing program' },
          { value: 'continuity', label: 'Continuity of operations' },
          { value: 'tracking', label: 'Patient tracking system' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'When was your emergency plan last reviewed and updated?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: '1_2_years', label: '1-2 years ago' },
          { value: 'over_2', label: 'Over 2 years ago' },
          { value: 'never', label: 'Never updated' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'within_year' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'over_2', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan meets 42 CFR 484.102 requirements.',
      atRisk: 'Emergency plan needs updating. CMS requires annual review and update.',
      nonCompliant: 'No emergency preparedness plan. This is a Condition-level deficiency for Medicare certification.',
      unknown: 'Review emergency preparedness requirements under 42 CFR 484.102.',
    },
    stateNotes: [
      'Alabama is prone to tornadoes and severe weather - ensure plan addresses regional hazards',
      'Medicare requires annual training and testing of emergency procedures',
    ],
  },

  {
    id: 'AL-A10',
    itemId: 'A10',
    stateCode: 'AL',
    title: 'Quality Assessment and Performance Improvement (42 CFR 484.65)',
    description: 'Medicare requires an ongoing QAPI program',
    severity: 'high',
    regulationCode: '42 CFR 484.65',
    regulationName: 'QAPI',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a formal Quality Assessment and Performance Improvement (QAPI) program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which QAPI elements are in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'data_collection', label: 'Data collection on quality indicators' },
          { value: 'outcome_measures', label: 'Patient outcome measures tracked' },
          { value: 'action_plans', label: 'Performance improvement action plans' },
          { value: 'governing_body', label: 'Governing body oversight of QAPI' },
          { value: 'infection_surveillance', label: 'Infection surveillance' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'How often do you conduct QAPI reviews?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'No regular reviews' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
        { condition: 'includes', questionId: 'q3', value: ['monthly', 'quarterly'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'never', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets 42 CFR 484.65 requirements with regular reviews.',
      atRisk: 'QAPI reviews should be conducted at least quarterly.',
      nonCompliant: 'No QAPI program. This is a Condition-level deficiency for Medicare certification.',
      unknown: 'Review QAPI requirements under 42 CFR 484.65.',
    },
    stateNotes: [
      'QAPI required per 42 CFR 484.65 for Medicare certification',
      'Governing body must oversee QAPI activities',
      'Must track quality indicators and patient outcomes',
      'Performance improvement projects should address identified issues',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'AL-B1',
    itemId: 'B1',
    stateCode: 'AL',
    title: 'Personnel File Requirements (42 CFR 484.80)',
    description: 'Federal requirements for personnel documentation',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Personnel',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY staff member\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'background', label: 'Criminal background check clearance' },
          { value: 'tb', label: 'TB screening documentation' },
          { value: 'licenses', label: 'Current professional licenses (RN, LPN, PT, etc.)' },
          { value: 'references', label: 'Reference verification' },
          { value: 'orientation', label: 'Orientation completion documentation' },
          { value: 'competency', label: 'Competency evaluation records' },
          { value: 'job_desc', label: 'Signed job description' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per 42 CFR 484.80.',
      atRisk: 'Some personnel files may be missing required documents. Review all files for completeness. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation. CMS surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against federal requirements.',
    },
    stateNotes: [
      'Federal requirements apply per 42 CFR 484.80 for Medicare agencies',
      'Alabama does not impose additional personnel file requirements',
      'I-9 Employment Eligibility required per federal law',
      'Background check documentation required (OIG, state, registry)',
    ],
  },

  {
    id: 'AL-B2',
    itemId: 'B2',
    stateCode: 'AL',
    title: 'Professional License Verification',
    description: 'All licensed personnel must have current, verified credentials',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Personnel',
    agencyName: 'Alabama Board of Nursing',
    agencyAbbr: 'ABN',
    agencyPhone: '(334) 293-5200',
    agencyUrl: 'https://www.abn.alabama.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all professional licenses (RN, LPN, PT, OT, etc.) are current before staff provide services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified before first shift' },
          { value: 'sometimes', label: 'Sometimes / not consistently' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and renew verifications?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, with tracking system' },
          { value: 'no', label: 'No / manual only' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification processes meet requirements.',
      atRisk: 'License verification is inconsistent. Verify all professional licenses at abn.alabama.gov before staff work.',
      nonCompliant: 'No license verification process. Staff with expired or invalid licenses violate federal requirements.',
      unknown: 'Verify all professional licenses are current through Alabama Board of Nursing.',
    },
    stateNotes: [
      'Alabama Board of Nursing: abn.alabama.gov - (334) 293-5200',
      'RN and LPN licenses require verification before patient care',
      'Physical therapists: pt.alabama.gov',
      'Occupational therapists: ot.alabama.gov',
    ],
  },

  {
    id: 'AL-B3',
    itemId: 'B3',
    stateCode: 'AL',
    title: 'Home Health Aide Qualifications (42 CFR 484.80)',
    description: 'Federal requirements for home health aide training and competency',
    severity: 'high',
    regulationCode: '42 CFR 484.80(h)',
    regulationName: 'Home Health Aide',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do all home health aides meet federal training requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes - all have 75+ hours training and competency testing' },
          { value: 'some', label: 'Some may not have complete documentation' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Medicare requires 75 hours of training plus competency evaluation per 42 CFR 484.80(h).',
      },
      {
        id: 'q2',
        question: 'Do you provide and document 12 hours of annual in-service training for aides?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Are aide competency evaluations completed and documented annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Home health aide qualifications meet 42 CFR 484.80(h) requirements.',
      atRisk: 'Annual in-service training (12 hours) and competency evaluations are required. Update records.',
      nonCompliant: 'Aides do not meet federal training requirements. This is a Condition-level deficiency.',
      unknown: 'Review aide qualifications against 42 CFR 484.80(h).',
    },
    stateNotes: [
      'Federal 75-hour training requirement per 42 CFR 484.80(h)',
      'Alabama follows federal requirements - no additional state training mandate',
      '12 hours annual in-service training required',
      'Competency evaluation by RN required annually',
    ],
  },

  {
    id: 'AL-B4',
    itemId: 'B4',
    stateCode: 'AL',
    title: 'Job Descriptions',
    description: 'Written job descriptions for all positions',
    severity: 'medium',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Personnel',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does every employee have a written job description on file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do job descriptions include required qualifications, duties, and supervision requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do employees sign acknowledgment of their job description?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Job descriptions are complete and signed for all employees.',
      atRisk: 'Job descriptions may be incomplete or unsigned. Review and update all job descriptions.',
      nonCompliant: 'Written job descriptions are missing. Create job descriptions for all positions.',
      unknown: 'Review job description requirements.',
    },
    stateNotes: [
      'Job descriptions required per 42 CFR 484.80 for Medicare certification',
      'Must include qualifications, duties, and supervision requirements',
      'Employee acknowledgment signature recommended',
      'Update when job duties change significantly',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'AL-C1',
    itemId: 'C1',
    stateCode: 'AL',
    title: 'Criminal Background Check',
    description: 'Background screening for all patient care staff',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Personnel',
    agencyName: 'Alabama Law Enforcement Agency',
    agencyAbbr: 'ALEA',
    agencyPhone: '(334) 517-2450',
    agencyUrl: 'https://www.alea.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Which background checks do you conduct for ALL patient care staff? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'state', label: 'Alabama state criminal background check (ALEA)' },
          { value: 'fbi', label: 'FBI fingerprint-based check' },
          { value: 'nar', label: 'Nurse Aide Registry check' },
          { value: 'sex_offender', label: 'Sex Offender Registry check' },
          { value: 'oig', label: 'OIG/SAM exclusion list check' },
        ],
        required: true,
        helpText: 'Federal requirements mandate screening against exclusion lists. State checks are best practice.',
      },
      {
        id: 'q2',
        question: 'Do any staff begin working with patients BEFORE their background check is completed?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation - no patient contact allowed before clearance)
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required checks
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if 2-3 checks but not all required
      { condition: 'countBetween', questionId: 'q1', value: [2, 3], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is comprehensive for all staff.',
      atRisk: 'Background screening may be incomplete. Review procedures and ensure all required checks are completed.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per 42 CFR 484.80, no patient contact is permitted until background checks are completed.',
      unknown: 'Audit all staff files for background check documentation.',
    },
    stateNotes: [
      'Alabama does not mandate specific background checks for home health, but federal requirements apply',
      'OIG exclusion list check is REQUIRED for Medicare/Medicaid billing',
      'State criminal check through ALEA is best practice',
    ],
  },

  {
    id: 'AL-C2',
    itemId: 'C2',
    stateCode: 'AL',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal requirement - must check exclusion lists before hiring and monthly thereafter',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list (LEIE) before hiring?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often do you re-check the OIG/SAM exclusion lists for current employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'annually', label: 'Annually' },
          { value: 'never', label: 'Only at hire' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you check the SAM.gov exclusion database?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'monthly' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['quarterly', 'annually'], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion list checks are completed at hire and monthly.',
      atRisk: 'OIG recommends monthly screening. Increase frequency to monthly.',
      nonCompliant: 'OIG exclusion checks are not being conducted. Billing for services by excluded individuals is Medicare fraud.',
      unknown: 'Check exclusion list requirements at exclusions.oig.hhs.gov.',
    },
    stateNotes: [
      'OIG LEIE: exclusions.oig.hhs.gov - check before hire and monthly',
      'SAM.gov exclusion database also required',
      'Civil monetary penalties for employing excluded individuals',
      'Document all exclusion checks in personnel files',
    ],
  },

  {
    id: 'AL-C3',
    itemId: 'C3',
    stateCode: 'AL',
    title: 'Nurse Aide Registry Check',
    description: 'Must verify aides against state registry for abuse findings',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Aide Registry',
    agencyName: 'Alabama Department of Public Health',
    agencyAbbr: 'ADPH',
    agencyPhone: '(334) 206-5300',
    agencyUrl: 'https://cv.adph.state.al.us/TBW/NAR/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Alabama Nurse Aide Registry before hiring aides?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No / not aware of this requirement' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document the registry check in the employee file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Nurse Aide Registry checks are conducted and documented.',
      atRisk: 'Registry checks may be inconsistent or undocumented. Check all aides and document results.',
      nonCompliant: 'Federal requirements mandate registry checks for aides. Check at cv.adph.state.al.us/TBW/NAR/.',
      unknown: 'Verify Registry check procedures.',
    },
    stateNotes: [
      'Alabama Nurse Aide Registry: cv.adph.state.al.us/TBW/NAR/',
      'ADPH maintains registry - (334) 206-5300',
      'Check for abuse findings before hiring aides',
      'Federal requirement per 42 CFR 484.80',
    ],
  },

  {
    id: 'AL-C4',
    itemId: 'C4',
    stateCode: 'AL',
    title: 'DHR Elder Abuse Registry Check (Shirley\'s Law)',
    description: 'Alabama requires service providers to query the Elder Abuse Registry for all employees per Code of Alabama § 38-9G-5',
    severity: 'high',
    regulationCode: '38-9G-5',
    regulationName: 'Abuse Registry',
    agencyName: 'Alabama Department of Human Resources',
    agencyAbbr: 'DHR',
    agencyPhone: '1-800-458-7214',
    agencyUrl: 'https://dhr.alabama.gov/adult-protective-services/alabama-elder-and-adult-in-need-of-protective-services-abuse-registry/',
    questions: [
      {
        id: 'q1',
        question: 'Do you query the Alabama DHR Elder Abuse Registry before hiring staff?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No / Not aware of this requirement' },
        ],
        required: true,
        helpText: 'Shirley\'s Law (Act 2022-161) requires home health agencies to query the DHR registry for all employees and prospective employees as of July 1, 2023.',
      },
      {
        id: 'q2',
        question: 'Do you document the registry clearance in the employee\'s file?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Have you checked ALL current employees against the registry?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - all current staff cleared' },
          { value: 'no', label: 'No / Not yet' },
        ],
        required: true,
        helpText: 'The law requires checking both prospective and current employees.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'DHR Elder Abuse Registry checks are conducted for all employees per Code of Alabama § 38-9G-5 (Shirley\'s Law).',
      atRisk: 'Registry checks may be inconsistent or undocumented. Query the DHR registry for all current and prospective employees. Call 1-800-458-7214 for clearance forms.',
      nonCompliant: 'Home health agencies are required to query the DHR Elder Abuse Registry per Shirley\'s Law (Code of Alabama § 38-9G-5). Contact DHR at 1-800-458-7214 immediately.',
      unknown: 'Verify compliance with Shirley\'s Law registry requirements at dhr.alabama.gov.',
    },
    stateNotes: [
      'Shirley\'s Law (Act 2022-161) effective July 1, 2023 for service providers',
      'Home health agencies must query registry for ALL employees and prospective employees',
      'Registry includes: criminal convictions, protection orders, and DHR abuse findings',
      'DHR Registry Clearance Forms available at dhr.alabama.gov',
      'Named after Shirley Johnson Smith, an elder abuse victim',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'AL-D1',
    itemId: 'D1',
    stateCode: 'AL',
    title: 'TB Screening',
    description: 'CDC recommends TB screening at hire for healthcare personnel',
    severity: 'high',
    regulationCode: 'CDC Guidelines',
    regulationName: 'TB Screening',
    agencyName: 'Centers for Disease Control and Prevention',
    agencyAbbr: 'CDC',
    agencyPhone: '(800) 232-4636',
    agencyUrl: 'https://www.cdc.gov/tb/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL patient care staff have TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff screened at hire' },
          { value: 'most', label: 'Most do, some may need updating' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'CDC recommends baseline TB screening at hire for all healthcare personnel.',
      },
      {
        id: 'q2',
        question: 'What type of TB screening do you use?',
        inputType: 'select',
        options: [
          { value: 'igra', label: 'IGRA blood test (QuantiFERON or T-SPOT)' },
          { value: 'tst', label: 'Tuberculin skin test (TST)' },
          { value: 'either', label: 'Either accepted' },
          { value: 'none', label: 'No standardized screening' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - Check for non-compliance first
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - Check for full compliance
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['igra', 'tst', 'either'] },
      ], result: 'COMPLIANT' },
      // 3. AT_RISK - Partial compliance
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff per CDC guidelines.',
      atRisk: 'Some TB screenings may need updating. Review all staff files.',
      nonCompliant: 'Staff are working without TB screening. Implement baseline screening for all patient care staff.',
      unknown: 'Review TB screening requirements per CDC guidelines.',
    },
    stateNotes: [
      'CDC recommends TB screening at hire - annual screening NOT required unless exposure or ongoing transmission',
      'Alabama does not have state-specific TB screening requirements for home health',
      'IGRA blood tests or TST skin tests are acceptable',
    ],
  },

  {
    id: 'AL-D2',
    itemId: 'D2',
    stateCode: 'AL',
    title: 'Infection Control Program (42 CFR 484.70)',
    description: 'Medicare requires infection prevention and control program',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Infection Control',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written infection prevention and control program that includes: (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Written infection control policies' },
          { value: 'hand_hygiene', label: 'Hand hygiene procedures' },
          { value: 'ppe', label: 'PPE use guidelines' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'training', label: 'Staff education on infection control' },
          { value: 'antibiotic', label: 'Antibiotic stewardship (if applicable)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a designated infection preventionist or responsible person?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection prevention program meets 42 CFR 484.70 requirements.',
      atRisk: 'Infection control program may be incomplete. Review and update to include all required elements.',
      nonCompliant: 'Infection control program is missing critical elements. This is a Condition-level deficiency.',
      unknown: 'Review infection control program against 42 CFR 484.70 requirements.',
    },
    stateNotes: [
      'Infection control required per 42 CFR 484.70 for Medicare certification',
      'Must designate infection preventionist or responsible person',
      'Surveillance, policies, and staff education required',
      'ADPH can provide guidance on infection control best practices',
    ],
  },

  {
    id: 'AL-D3',
    itemId: 'D3',
    stateCode: 'AL',
    title: 'Bloodborne Pathogen Exposure Plan',
    description: 'OSHA-required exposure control plan',
    severity: 'high',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written Exposure Control Plan for bloodborne pathogens?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is the plan reviewed and updated annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you offer Hepatitis B vaccination to at-risk employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Bloodborne pathogen exposure control plan meets OSHA requirements.',
      atRisk: 'Exposure control plan needs updating or Hepatitis B vaccination not offered.',
      nonCompliant: 'No written Exposure Control Plan. This violates OSHA 29 CFR 1910.1030.',
      unknown: 'Review OSHA bloodborne pathogen requirements.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.1030 applies to all healthcare employers',
      'Written Exposure Control Plan must be reviewed annually',
      'Hepatitis B vaccination must be offered at no cost to employees',
      'Post-exposure evaluation and follow-up required',
    ],
  },

  {
    id: 'AL-D4',
    itemId: 'D4',
    stateCode: 'AL',
    title: 'PPE Availability',
    description: 'Personal Protective Equipment must be available to staff',
    severity: 'high',
    regulationCode: '29 CFR 1910.132',
    regulationName: 'PPE',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Is PPE readily available to all patient care staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which PPE do you provide? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'gloves', label: 'Gloves' },
          { value: 'masks', label: 'Masks (surgical and N95)' },
          { value: 'gowns', label: 'Gowns' },
          { value: 'eye', label: 'Eye protection' },
          { value: 'hand_sanitizer', label: 'Hand sanitizer' },
        ],
      },
      {
        id: 'q3',
        question: 'Are staff trained on proper PPE use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'PPE is available and staff are properly trained.',
      atRisk: 'PPE training needs improvement. Ensure all staff know proper use.',
      nonCompliant: 'PPE is not readily available. This violates OSHA requirements.',
      unknown: 'Review PPE requirements and availability.',
    },
    stateNotes: [
      'OSHA 29 CFR 1910.132 requires PPE be provided at no cost',
      'Staff must be trained on proper PPE use before patient care',
      'Gloves, masks, gowns, and eye protection for infection control',
      'PPE must be appropriate for hazards encountered',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'AL-E1',
    itemId: 'E1',
    stateCode: 'AL',
    title: 'Orientation Training',
    description: 'All new staff must complete orientation before patient contact',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Orientation',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new staff complete orientation training covering the following BEFORE patient contact? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Agency policies and procedures' },
          { value: 'patient_rights', label: 'Patient rights' },
          { value: 'infection', label: 'Infection control' },
          { value: 'safety', label: 'Safety procedures' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'documentation', label: 'Documentation requirements' },
          { value: 'hipaa', label: 'HIPAA/Privacy' },
          { value: 'abuse', label: 'Abuse recognition and reporting' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training program is comprehensive.',
      atRisk: 'Orientation training may be incomplete. Review and expand training content.',
      nonCompliant: 'Staff are providing services without required orientation training. Implement comprehensive orientation immediately.',
      unknown: 'Review orientation training program.',
    },
    stateNotes: [
      'Orientation required per 42 CFR 484.80 before patient contact',
      'Must include policies, infection control, safety, and patient rights',
      'HIPAA training required at orientation',
      'Document completion in personnel file',
    ],
  },

  {
    id: 'AL-E2',
    itemId: 'E2',
    stateCode: 'AL',
    title: 'Abuse/Neglect Reporting Training',
    description: 'Alabama law requires reporting of adult abuse and neglect',
    severity: 'high',
    regulationCode: '38-9-8',
    regulationName: 'Mandated Reporting',
    agencyName: 'Alabama Department of Human Resources',
    agencyAbbr: 'DHR',
    agencyPhone: '1-800-458-7214',
    agencyUrl: 'https://dhr.alabama.gov/adult-protective-services/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive training on abuse/neglect recognition and mandatory reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'no', label: 'No / not consistently' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know the Alabama Adult Protective Services hotline (1-800-458-7214)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on abuse/neglect reporting per Code of Alabama 38-9-8.',
      atRisk: 'Ensure all staff know mandatory reporting requirements and DHR hotline: 1-800-458-7214.',
      nonCompliant: 'Staff are not trained on mandatory abuse reporting. This violates Alabama law.',
      unknown: 'Verify all staff have received abuse reporting training.',
    },
    stateNotes: [
      'Alabama Adult Protective Services Hotline: 1-800-458-7214',
      'Healthcare workers are mandatory reporters under Code of Alabama 38-9-8',
      'Reports must be made immediately upon suspicion',
    ],
  },

  {
    id: 'AL-E3',
    itemId: 'E3',
    stateCode: 'AL',
    title: 'CPR/First Aid Certification',
    description: 'Current certifications for patient care staff',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Staff Qualifications',
    agencyName: 'N/A',
    agencyAbbr: 'N/A',
    agencyPhone: 'N/A',
    agencyUrl: 'N/A',
    questions: [
      {
        id: 'q1',
        question: 'Do all patient care staff have current CPR certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have current CPR' },
          { value: 'most', label: 'Most do, some are expired' },
          { value: 'no', label: 'No / Not required' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are CPR certifications from approved providers (AHA, Red Cross)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All patient care staff have current CPR certifications.',
      atRisk: 'Some CPR certifications are expired. Schedule recertification immediately.',
      nonCompliant: 'CPR certifications are not current for patient care staff.',
      unknown: 'Review CPR certification requirements.',
    },
    stateNotes: [
      'CPR certification recommended for all patient care staff',
      'American Heart Association and Red Cross certifications accepted',
      'Track certification expiration dates for timely renewal',
      'BLS for Healthcare Providers course recommended',
    ],
  },

  {
    id: 'AL-E4',
    itemId: 'E4',
    stateCode: 'AL',
    title: 'HIPAA Privacy Training',
    description: 'Training on patient privacy and confidentiality',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA Privacy',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do all employees receive HIPAA privacy training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees at hire and annually' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No formal HIPAA training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is HIPAA training documented in personnel files?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA privacy training is provided to all employees.',
      atRisk: 'HIPAA training should be provided annually, not just at hire.',
      nonCompliant: 'No HIPAA training provided. This violates federal privacy requirements.',
      unknown: 'Review HIPAA training requirements.',
    },
    stateNotes: [
      'HIPAA training required per 45 CFR 164 for all workforce members',
      'Training should be provided at hire and periodically thereafter',
      'Document training completion in personnel files',
      'HHS Office for Civil Rights enforces HIPAA: hhs.gov/hipaa',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'AL-F1',
    itemId: 'F1',
    stateCode: 'AL',
    title: 'Plan of Care (42 CFR 484.60)',
    description: 'Medicare requires individualized plan of care for each patient',
    severity: 'high',
    regulationCode: '42 CFR 484.60',
    regulationName: 'Plan of Care',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your care plans include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Diagnoses' },
          { value: 'medications', label: 'Medications' },
          { value: 'services', label: 'Services to be provided' },
          { value: 'frequency', label: 'Frequency and duration' },
          { value: 'goals', label: 'Measurable goals and outcomes' },
          { value: 'md_signature', label: 'Physician signature' },
        ],
      },
      {
        id: 'q3',
        question: 'Are care plans reviewed at least every 60 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet 42 CFR 484.60 requirements.',
      atRisk: 'Care plans may be incomplete or created after services begin. Plans must be in place before first visit.',
      nonCompliant: 'Services are being provided without written care plans. This is a Condition-level deficiency.',
      unknown: 'Review care plan process against 42 CFR 484.60 requirements.',
    },
    stateNotes: [
      'Plan of care required per 42 CFR 484.60 before services begin',
      'Must be physician-signed and include all required elements',
      'Review and update at least every 60 days',
      'Patient-specific goals and interventions required',
    ],
  },

  {
    id: 'AL-F2',
    itemId: 'F2',
    stateCode: 'AL',
    title: 'Clinical Supervision (42 CFR 484.80)',
    description: 'RN supervision required for aides and clinical staff',
    severity: 'high',
    regulationCode: '42 CFR 484.80(h)',
    regulationName: 'Aide Supervision',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do RNs provide supervision of home health aides as required?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - supervisory visit every 14 days' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No formal supervision' },
        ],
        required: true,
        helpText: 'Medicare requires RN supervisory visit every 14 days for skilled patients, or every 60 days for non-skilled.',
      },
      {
        id: 'q2',
        question: 'Are supervisory visits documented in the patient record?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Clinical supervision meets 42 CFR 484.80 requirements.',
      atRisk: 'Supervision may be inconsistent or undocumented. Ensure supervisory visits are completed per schedule.',
      nonCompliant: 'Required clinical supervision is not being provided. This is a Condition-level deficiency.',
      unknown: 'Review supervision procedures against federal requirements.',
    },
    stateNotes: [
      'RN supervisory visit every 14 days for skilled patients per 42 CFR 484.80(h)',
      'Every 60 days for non-skilled patients',
      'Supervision must be documented in patient record',
      'Aide must be observed providing services during supervision',
    ],
  },

  {
    id: 'AL-F3',
    itemId: 'F3',
    stateCode: 'AL',
    title: 'Comprehensive Assessment (42 CFR 484.55)',
    description: 'OASIS assessment requirements for Medicare patients',
    severity: 'high',
    regulationCode: '42 CFR 484.55',
    regulationName: 'Assessment',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete OASIS assessments for all Medicare/Medicaid patients?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, at all required timepoints' },
          { value: 'sometimes', label: 'Sometimes miss timepoints' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are OASIS assessments completed by qualified RNs or therapists?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not sure' },
        ],
      },
      {
        id: 'q3',
        question: 'Are assessments transmitted to CMS within required timeframes?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Sometimes late' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'OASIS assessments are completed and transmitted per requirements.',
      atRisk: 'OASIS assessments or transmissions may be delayed. Review process.',
      nonCompliant: 'OASIS assessments not being completed. This affects reimbursement and is a Condition-level deficiency.',
      unknown: 'Review OASIS requirements.',
    },
    stateNotes: [
      'OASIS required per 42 CFR 484.55 for Medicare/Medicaid patients',
      'Must be completed by qualified RN or therapist',
      'Transmission to CMS required within 30 days of assessment',
      'OASIS impacts PDGM reimbursement and quality scores',
    ],
  },

  {
    id: 'AL-F4',
    itemId: 'F4',
    stateCode: 'AL',
    title: 'Patient Rights (42 CFR 484.50)',
    description: 'Patient rights notification requirements',
    severity: 'high',
    regulationCode: '42 CFR 484.50',
    regulationName: 'Patient Rights',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to all patients at admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you provide hotline numbers for complaints (state and CMS)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do patients sign acknowledgment of receiving rights information?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided with hotline numbers and documented acknowledgment.',
      atRisk: 'Obtain signed acknowledgment of patient rights from all patients.',
      nonCompliant: 'Patient rights or complaint hotlines not provided. This is a Condition-level deficiency.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      'Patient rights required per 42 CFR 484.50 for Medicare certification',
      'Must include state and CMS complaint hotlines',
      'Alabama does not have state-specific home health patient rights',
      'CMS 1-800-MEDICARE and QIO contact info required',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'AL-G1',
    itemId: 'G1',
    stateCode: 'AL',
    title: 'Record Retention',
    description: 'Federal requirements for medical record retention',
    severity: 'medium',
    regulationCode: '42 CFR 484.110',
    regulationName: 'Clinical Records',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain clinical records after discharge?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Medicare requires retention for 5 years after discharge. State law may require longer.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention policy meets federal requirements (5 years minimum).',
      atRisk: 'Federal requirements mandate 5-year retention. Update your policy.',
      nonCompliant: 'Record retention policy is inadequate. Medicare requires 5-year retention after discharge.',
      unknown: 'Review record retention requirements under 42 CFR 484.110.',
    },
    stateNotes: [
      'Alabama follows federal requirements - no additional state retention mandate for home health',
      'Medicare requires 5 years after discharge',
      'For minors, retain until patient reaches age of majority plus additional years',
    ],
  },

  {
    id: 'AL-G2',
    itemId: 'G2',
    stateCode: 'AL',
    title: 'Clinical Documentation Timeliness',
    description: 'Complete clinical documentation for all patient visits',
    severity: 'high',
    regulationCode: '42 CFR 484.110',
    regulationName: 'Clinical Records',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are visit notes completed and signed for every patient visit?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 24-48 hours' },
          { value: 'usually', label: 'Usually, some delays' },
          { value: 'inconsistent', label: 'Inconsistent documentation' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do visit notes include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'date_time', label: 'Date and time of visit' },
          { value: 'services', label: 'Services provided' },
          { value: 'patient_response', label: 'Patient response/condition' },
          { value: 'signature', label: 'Caregiver signature' },
          { value: 'duration', label: 'Visit duration' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'inconsistent', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation is complete and timely.',
      atRisk: 'Some documentation delays. Complete all notes within 24-48 hours.',
      nonCompliant: 'Inconsistent clinical documentation. This affects billing and creates compliance risk.',
      unknown: 'Review clinical documentation requirements.',
    },
    stateNotes: [
      'Documentation required per 42 CFR 484.110 for all visits',
      'Notes should be completed within 24-48 hours of visit',
      'Must include date, time, services, and patient response',
      'Documentation supports billing claims and quality review',
    ],
  },

  {
    id: 'AL-G3',
    itemId: 'G3',
    stateCode: 'AL',
    title: 'HIPAA Privacy Compliance',
    description: 'Patient privacy and information security requirements',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a designated HIPAA Privacy Officer?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have policies for the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'phi', label: 'PHI access and disclosure' },
          { value: 'breach', label: 'Breach notification' },
          { value: 'authorization', label: 'Patient authorization forms' },
          { value: 'min_necessary', label: 'Minimum necessary standard' },
          { value: 'baa', label: 'Business Associate Agreements' },
        ],
      },
      {
        id: 'q3',
        question: 'How are patient records protected?',
        inputType: 'select',
        options: [
          { value: 'electronic_secure', label: 'Electronic with encryption and access controls' },
          { value: 'paper_locked', label: 'Paper in locked storage' },
          { value: 'both', label: 'Both with proper security' },
          { value: 'unsecure', label: 'Minimal security measures' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
        { condition: 'includes', questionId: 'q3', value: ['electronic_secure', 'paper_locked', 'both'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'unsecure', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA privacy compliance is comprehensive.',
      atRisk: 'Designate a HIPAA Privacy Officer and strengthen privacy policies.',
      nonCompliant: 'Patient records are not properly secured. This violates HIPAA.',
      unknown: 'Review HIPAA privacy requirements at hhs.gov/hipaa.',
    },
    stateNotes: [
      'HIPAA Privacy Rule applies per 45 CFR 164',
      'Privacy Officer designation required',
      'Business Associate Agreements required for all vendors handling PHI',
      'Breach notification required within 60 days',
    ],
  },

  {
    id: 'AL-G4',
    itemId: 'G4',
    stateCode: 'AL',
    title: 'Billing Documentation',
    description: 'Documentation supporting billing claims',
    severity: 'high',
    regulationCode: '42 CFR 424',
    regulationName: 'Claims Documentation',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does clinical documentation support all billed services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, documentation always supports billing' },
          { value: 'usually', label: 'Usually' },
          { value: 'inconsistent', label: 'Inconsistent or unclear' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you perform regular audits of billing documentation?',
        inputType: 'select',
        options: [
          { value: 'regularly', label: 'Yes, at least quarterly' },
          { value: 'occasionally', label: 'Occasionally' },
          { value: 'never', label: 'No audits' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'regularly' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q2', value: 'occasionally' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'inconsistent', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Billing documentation is complete and regularly audited.',
      atRisk: 'Implement regular billing audits to ensure documentation supports claims.',
      nonCompliant: 'Billing documentation is inconsistent. This creates fraud and abuse risk.',
      unknown: 'Review billing documentation requirements.',
    },
    stateNotes: [
      'Documentation must support all billed services per 42 CFR 424',
      'False Claims Act penalties for fraudulent billing',
      'Regular internal audits recommended (at least quarterly)',
      'Keep billing documentation for minimum 5 years',
    ],
  },

  {
    id: 'AL-G5',
    itemId: 'G5',
    stateCode: 'AL',
    title: 'Document Expiration Tracking',
    description: 'System to track expiration of licenses, certifications, and clearances',
    severity: 'high',
    regulationCode: 'Best Practice',
    regulationName: 'Compliance',
    agencyName: 'N/A',
    agencyAbbr: 'N/A',
    agencyPhone: 'N/A',
    agencyUrl: 'N/A',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'medicare_cert', label: 'Medicare certification' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'tb', label: 'TB screenings' },
          { value: 'cpr', label: 'CPR certifications' },
          { value: 'licenses', label: 'Professional licenses (RN, LPN, PT, etc.)' },
          { value: 'background', label: 'Background check renewals' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Missing tracking for: {missing}.',
      nonCompliant: 'No systematic document tracking. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'Track all expiring documents: licenses, certifications, insurance, background checks',
      'Set reminders 30-60 days before expiration',
      'Compliance software can automate tracking',
      'Expired credentials can result in survey deficiencies and claim denials',
    ],
  },
];

export default ALABAMA_QUESTIONS;